"use client"
import cn from "classnames"
import React, { ReactNode } from "react"

interface CarouselClassNames {
    itemNext2?: string
    itemPrev2?: string
    dots?: {
        root: string
        active: string
        item: string
    }
    item: string
    itemActive: string
    itemNext: string
    itemPrev: string
    root: string
}

interface CarouselProps {
    onChange?: (index: number) => void
    classNames: CarouselClassNames
    prevBtn?: (onClick: () => void) => React.ReactNode
    nextBtn?: (onClick: () => void) => React.ReactNode
    startAt: number
    autoplayTime?: number
    activeSlide?: number
    heightProvider?: ReactNode
    items: React.ReactNode[]
}

interface CarouselState {
    prevIndex: number
    nextIndex: number
    next2: number
    prev2: number
    activeIndex: number
}

export class Carousel extends React.Component<CarouselProps, CarouselState> {
    private timer?: number | NodeJS.Timeout
    private observer?: ResizeObserver
    private elements: HTMLElement[] = []
    private touchStart: number | null = null
    private readonly rootRef: React.RefObject<HTMLDivElement>

    constructor(props: CarouselProps) {
        super(props)
        const [prevIndex, nextIndex] = this.getNextPrev(props.startAt)
        const [_, next2] = this.getNextPrev(nextIndex)
        const [prev2, __] = this.getNextPrev(prevIndex)
        this.state = {
            prevIndex,
            nextIndex,
            next2,
            prev2,
            activeIndex: props.startAt
        }

        this.rootRef = React.createRef()
    }

    componentDidMount() {
        if (this.props.autoplayTime) {
            this.autoplay()
        }
        this.props.onChange?.(this.state.activeIndex)
        this.observer = new ResizeObserver(this.onObserve)
        if (this.rootRef.current && !this.props.heightProvider) {
            this.elements = Array.from(this.rootRef.current.getElementsByClassName(this.props.classNames.item)) as HTMLElement[]
            this.elements.forEach((i) => this.observer?.observe(i))
        }
    }

    componentDidUpdate(prevProps: Readonly<CarouselProps>, prevState: Readonly<CarouselState>) {
        if (prevState.activeIndex !== this.state.activeIndex) {
            this.props.onChange?.(this.state.activeIndex)
        }
        if (prevProps.activeSlide !== this.props.activeSlide && typeof this.props.activeSlide === "number") {
            this.goTo(this.props.activeSlide)
        }
        if (prevProps.items !== this.props.items && !this.props.heightProvider) {
            this.elements.forEach((i) => this.observer?.unobserve(i))
            if (this.rootRef.current) {
                this.elements = Array.from(this.rootRef.current.getElementsByClassName(this.props.classNames.item)) as HTMLElement[]
                this.elements.forEach((i) => this.observer?.observe(i))
            }
        }
    }

    componentWillUnmount() {
        if (this.timer) {
            clearTimeout(this.timer as any)
        }
        this.observer?.disconnect()
    }

    onObserve = (entries: ResizeObserverEntry[]) => {
        if (this.rootRef.current && !this.props.heightProvider) {
            const maxHeight = Math.max(0, ...entries.map((i) => i?.borderBoxSize?.[0]?.blockSize))
            this.rootRef.current.style.height = maxHeight + "px"
        }
    }

    autoplay = () => {
        if (this.timer) {
            clearTimeout(this.timer as any)
        }
        this.timer = setTimeout(() => {
            if (!document.hidden) {
                this.go(1)
            }
            this.autoplay()
        }, this.props.autoplayTime || 10000)
    }

    getNextPrev = (activeIndex: number): [number, number] => {
        const itemsLength = this.props.items.length
        let prev = activeIndex - 1
        let next = activeIndex + 1
        if (activeIndex === 0) {
            prev = itemsLength - 1
        }
        if (activeIndex === itemsLength - 1) {
            next = 0
        }
        return [prev, next]
    }

    go = (forEl: number) => {
        const oldIndex = this.state.activeIndex
        const itemsLength = this.props.items.length
        let activeIndex = oldIndex + forEl
        if (activeIndex < 0) {
            activeIndex = itemsLength - 1
        }
        if (activeIndex === itemsLength) {
            activeIndex = 0
        }
        const [prevIndex, nextIndex] = this.getNextPrev(activeIndex)
        const [_, next2] = this.getNextPrev(nextIndex)
        const [prev2, __] = this.getNextPrev(prevIndex)
        this.setState({
            activeIndex,
            prevIndex,
            next2,
            prev2,
            nextIndex
        })
    }

    onMouseLeave = () => {
        if (this.props.autoplayTime) {
            this.autoplay()
        }
    }

    onMouseEnter = () => {
        if (this.timer) {
            clearTimeout(this.timer as any)
        }
    }

    goTo = (activeIndex: number) => {
        if (this.state.activeIndex !== activeIndex) {
            const [prevIndex, nextIndex] = this.getNextPrev(activeIndex)
            const [_, next2] = this.getNextPrev(nextIndex)
            const [prev2, __] = this.getNextPrev(prevIndex)
            this.setState({
                activeIndex,
                prevIndex,
                next2,
                prev2,
                nextIndex
            })
        }
    }

    onTouchStart = (ev: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (this.timer) {
            clearTimeout(this.timer as any)
        }
        const key = Number((ev.currentTarget as HTMLElement).dataset.key)
        if (key === this.state.activeIndex) {
            if ("touches" in ev) {
                this.touchStart = (ev.touches[0] || ev.changedTouches[0]).clientX
            } else {
                this.touchStart = ev.clientX
            }
        } else {
            this.goTo(key)
            this.touchStart = null
        }
    }

    onTouchEnd = (ev: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (this.props.autoplayTime) {
            this.autoplay()
        }
        if (this.touchStart === null) return

        let x: number
        if ("changedTouches" in ev) {
            x = ev.changedTouches[0].clientX
        } else {
            x = ev.clientX
        }

        const diff = this.touchStart - x
        if (diff < -100) {
            this.go(-1)
        } else if (diff > 100) {
            this.go(1)
        }
        this.touchStart = null
    }

    render() {
        const { classNames, heightProvider, items, prevBtn, nextBtn } = this.props
        const { activeIndex, prevIndex, nextIndex, next2, prev2 } = this.state
        return (
            <div className={classNames.root} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave} ref={this.rootRef}>
                {heightProvider && <div style={{ visibility: "hidden", overflow: "hidden", pointerEvents: "none" }}>{heightProvider}</div>}
                {items.map((i, k) => {
                    const classSet: { [key: string]: boolean } = {
                        [classNames.itemActive]: k === activeIndex,
                        [classNames.itemNext]: k === nextIndex,
                        [classNames.itemPrev]: k === prevIndex,
                        [classNames.item]: true
                    }

                    if (classNames.itemNext2) {
                        classSet[classNames.itemNext2] = k === next2
                    }
                    if (classNames.itemPrev2) {
                        classSet[classNames.itemPrev2] = k === prev2
                    }
                    const className = cn(classSet)

                    return (
                        <div
                            key={k}
                            data-key={k}
                            className={className}
                            onMouseDown={this.onTouchStart}
                            onTouchStart={this.onTouchStart}
                            onTouchEnd={this.onTouchEnd}
                            onMouseUp={this.onTouchEnd}>
                            {i}
                        </div>
                    )
                })}

                {classNames.dots && (
                    <div className={classNames.dots.root}>
                        {items.map((_, k) => {
                            const className = cn({
                                [classNames.dots.active]: k === activeIndex,
                                [classNames.dots.item]: true
                            })
                            return <div key={k} className={className} onClick={() => this.goTo(k)} />
                        })}
                    </div>
                )}
                {prevBtn && prevBtn(() => this.go(-1))}
                {nextBtn && nextBtn(() => this.go(1))}
            </div>
        )
    }
}
